import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Check, ShieldCheck, ArrowLeft, AlertTriangle, Lock } from "lucide-react";
import { journeyApi } from "@/lib/journeyApi";
import { JourneyTopBar, JourneyFooter } from "@/components/JourneyChrome";

const FEATURES = [
  "Full core planning system",
  "Ongoing Paycheck Edits",
  "Protect, Eliminate, Build & Freedom planning",
  "Debt, protection and goal planning",
  "What If scenarios",
  "The Receipts and confirmed progress tracking",
  "Ongoing access",
];

export default function Checkout() {
  const [stripePromise, setStripePromise] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const cfg = await journeyApi.paymentsConfig();
        if (!active) return;
        if (!cfg.publishable_key) {
          setErrorMsg("Secure checkout is temporarily unavailable. Please try again shortly.");
          setStatus("error");
          return;
        }
        setStripePromise(loadStripe(cfg.publishable_key));
        setStatus("ready");
      } catch {
        if (!active) return;
        setErrorMsg("We couldn't load secure checkout. Please check your connection and try again.");
        setStatus("error");
      }
    })();
    return () => { active = false; };
  }, []);

  // Stripe calls this to create the embedded session; the server records the
  // transaction + browser claim cookie exactly like the hosted flow.
  const fetchClientSecret = useCallback(async () => {
    const { client_secret } = await journeyApi.checkoutEmbedded(window.location.origin);
    return client_secret;
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-[#FAF8F4] text-[#171717]" data-testid="checkout-page">
      <JourneyTopBar showSignIn={false} />
      <main className="mx-auto w-full max-w-[1440px] flex-1 px-5 py-9 sm:px-8 lg:py-14">
        <Link to="/" data-testid="checkout-back-link"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#2F6B57] transition-colors hover:text-[#164C3B]">
          <ArrowLeft className="h-4 w-4" /> Back to The Paycheck Edit
        </Link>

        <div className="mt-7 grid items-start gap-8 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.05fr)] lg:gap-12">
          {/* ---------------- Offer summary ---------------- */}
          <aside data-testid="checkout-summary"
            className="rounded-[1.75rem] border border-[#E5E0D9] bg-[#FCFBF8] p-7 sm:p-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#2F6B57]">The one-time offer</p>
            <h1 className="mt-3 font-serif text-[1.7rem] leading-tight text-[#164C3B]">The Paycheck Edit™</h1>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-serif text-[3.25rem] leading-none text-[#164C3B]" data-testid="checkout-price">$147</span>
              <span className="text-[15px] font-medium text-[#70584A]">one-time</span>
            </div>

            <div className="mt-6 h-px w-full bg-gradient-to-r from-[#C9A86A]/70 via-[#E5E0D9] to-transparent" />

            <ul className="mt-6 space-y-3 text-[15px] leading-snug text-[#3f3f3f]">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#DDE7DF]">
                    <Check className="h-3 w-3 text-[#164C3B]" strokeWidth={3} />
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <p className="mt-6 text-sm text-[#70584A]">No subscription. No bank connection required.</p>

            <div className="mt-5 flex items-start gap-3 rounded-2xl bg-[#DDE7DF]/55 px-4 py-3.5">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#164C3B]" />
              <div>
                <p className="font-serif text-[1.05rem] text-[#164C3B]">14-Day Money-Back Guarantee</p>
                <p className="mt-1 text-[13px] leading-relaxed text-[#525252]">
                  Your 14-day period begins when your payment is successfully charged.
                </p>
              </div>
            </div>

            <p className="mt-4 text-[13px] text-[#8A7C6B]" data-testid="checkout-us-only">
              Currently available to customers in the United States only.
            </p>
          </aside>

          {/* ---------------- Stripe embedded payment (minimal chrome) ---------------- */}
          <section className="min-w-0">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-serif text-xl text-[#164C3B]">Complete your purchase</h2>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#70584A]">
                <Lock className="h-3.5 w-3.5" /> Secure checkout
              </span>
            </div>

            {status === "loading" && (
              <div className="flex min-h-[340px] items-center justify-center rounded-2xl border border-[#E5E0D9] bg-white/60"
                data-testid="checkout-loading">
                <div className="text-center">
                  <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-[#E5E0D9] border-t-[#2F6B57]" />
                  <p className="mt-5 text-[15px] text-[#525252]">Loading secure checkout…</p>
                </div>
              </div>
            )}

            {status === "error" && (
              <div className="flex min-h-[340px] items-center justify-center rounded-2xl border border-[#E5E0D9] bg-white/60"
                data-testid="checkout-error">
                <div className="max-w-sm px-6 text-center">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#F3E4DE] text-[#8B3A2B]">
                    <AlertTriangle className="h-7 w-7" />
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-[#171717]">Checkout is unavailable right now</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-[#525252]">{errorMsg}</p>
                  <button type="button" onClick={() => window.location.reload()} data-testid="checkout-retry"
                    className="mt-6 inline-flex min-h-[48px] items-center justify-center rounded-full bg-[#2F6B57] px-7 text-sm font-semibold text-white transition-colors hover:bg-[#255544]">
                    Try again
                  </button>
                </div>
              </div>
            )}

            {status === "ready" && stripePromise && (
              // No outer card: Stripe Embedded Checkout carries its own container,
              // mounts directly on the ivory page, and grows with its content so
              // the page uses a single natural scroll (no nested scrollbar).
              <div data-testid="checkout-embedded-container">
                <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            )}

            <p className="mt-6 text-[13px] leading-relaxed text-[#70584A]" data-testid="checkout-policy-links">
              By purchasing you agree to our{" "}
              <Link to="/legal#terms" className="font-medium text-[#2F6B57] underline-offset-2 hover:underline">Terms</Link>,{" "}
              <Link to="/legal#privacy" className="font-medium text-[#2F6B57] underline-offset-2 hover:underline">Privacy</Link>,{" "}
              <Link to="/legal#disclaimer" className="font-medium text-[#2F6B57] underline-offset-2 hover:underline">Financial Disclaimer</Link>, and{" "}
              <Link to="/legal#refund" className="font-medium text-[#2F6B57] underline-offset-2 hover:underline">14-Day Refund Policy</Link>.
            </p>
            <p className="mt-2 text-xs text-[#8A7C6B]">
              Payments are securely processed by Stripe. The Paycheck Edit never sees or stores your card details.
            </p>
          </section>
        </div>
      </main>
      <JourneyFooter />
    </div>
  );
}
